import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { Button, Modal } from '../../components';
import { cx } from '../../lib/cx';
import { queryKeys } from '../../lib/queryKeys';
import { useCapability } from '../workspace/useCapability';
import { useSearch } from '../search/useSearch';
import { deleteRequest, mergeAsDuplicate, type RequestRow } from './api';

/**
 * The two things you can do to a request that are not editing it: merge it
 * into the one it duplicates, and delete it.
 *
 * Both live here rather than on the review banner, even though the review
 * banner is where the need for them shows up. The banner appears only while a
 * request is unreviewed, and a duplicate is just as often spotted a week later
 * by whoever is working the Board — hanging "this is the same as #3120" off a
 * control that has already disappeared would mean the only way to act on it is
 * to un-review the request first.
 *
 * Both are also deliberately undramatic to reach and deliberately hard to
 * complete. Merging asks which request, by searching for it. Deleting asks for
 * the request's number typed out, and the server asks for it again (see
 * DeleteRequestSchema) — it is the one irreversible action in the product.
 */

export interface RequestActionsProps {
  readonly workspaceId: string;
  readonly request: RequestRow;
  /**
   * Where these are being shown.
   *
   * `review` puts them beside Accept on the review banner, as the alternatives
   * to accepting — which is where the decision is actually being made, and
   * where someone looking at a duplicate or a mis-post is already standing.
   * `header` is the quieter copy for a request that has already been reviewed,
   * where merging and deleting are corrections rather than one of three ways
   * to answer the question on screen.
   *
   * Only one is ever rendered at a time; the screen decides which.
   */
  readonly variant?: 'header' | 'review';
}

export function RequestActions({ workspaceId, request, variant = 'header' }: RequestActionsProps) {
  const canReview = useCapability('request.review');
  const canAdmin = useCapability('workspace.admin');
  const [merging, setMerging] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Already merged: the banner on the screen says where it went, and offering
  // to merge it again is offering a refusal.
  const alreadyMerged = request.duplicateOf !== null;

  if (!canReview && !canAdmin) return null;

  // On the banner these are real alternatives to Accept and are dressed as
  // such; in the header they are corrections and stay out of the way.
  const onBanner = variant === 'review';
  // Quiet on the review strip too: Accept is the answer there, and these are
  // the alternatives beside it, not three buttons shouting at once.
  const mergeClass = onBanner
    ? 'rounded-md px-2 py-1 fs-12 font-medium whitespace-nowrap text-ink-soft hover:bg-accent-tint hover:text-ink'
    : 'rounded-md px-2 py-1 fs-11 font-semibold text-muted hover:bg-surface-2 hover:text-ink';
  const deleteClass = onBanner
    ? 'rounded-md px-2 py-1 fs-12 font-medium whitespace-nowrap text-ink-soft hover:bg-danger-tint hover:text-danger'
    : 'rounded-md px-2 py-1 fs-11 font-semibold text-muted hover:bg-danger-tint hover:text-danger';

  return (
    <>
      <div className={cx('flex flex-none gap-0.5', !onBanner && 'ml-auto')}>
        {canReview && !alreadyMerged && (
          <button type="button" onClick={() => setMerging(true)} className={mergeClass}>
            {onBanner ? 'Duplicate…' : 'Merge as duplicate'}
          </button>
        )}
        {canAdmin && (
          <button type="button" onClick={() => setDeleting(true)} className={deleteClass}>
            Delete
          </button>
        )}
      </div>

      {merging && (
        <MergeDialog workspaceId={workspaceId} request={request} open={merging} onOpenChange={setMerging} />
      )}
      {deleting && (
        <DeleteDialog
          workspaceId={workspaceId}
          request={request}
          open={deleting}
          onOpenChange={setDeleting}
        />
      )}
    </>
  );
}

/**
 * Pick the request this one duplicates, by searching for it.
 *
 * The existing workspace search, not a bespoke picker: it already ranks over
 * titles and message bodies, already applies the client/internal split
 * server-side, and already debounces. A duplicate is usually found by
 * remembering a phrase from it rather than its number, which is exactly what
 * that search is good at.
 */
function MergeDialog({
  workspaceId,
  request,
  open,
  onOpenChange,
}: {
  workspaceId: string;
  request: RequestRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<{ id: string; number: number; title: string } | null>(null);
  const search = useSearch(query);

  // Itself is never an option: the server refuses it, and offering it is a
  // trap set for a tired reviewer.
  const hits = (search.data?.requests ?? []).filter((hit) => hit.id !== request.id);

  const merge = useMutation({
    mutationFn: () => {
      if (picked === null) throw new Error('Pick the request this duplicates.');
      return mergeAsDuplicate(workspaceId, request.id, picked.id);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.request(workspaceId, request.id) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.requests(workspaceId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.reviewQueue(workspaceId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.board(workspaceId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.requestActivity(workspaceId, request.id) }),
      ]);
      onOpenChange(false);
    },
  });

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={`Merge #${request.number} as a duplicate`}
      description="Find the request this one is the same as."
      footer={
        <>
          <Button size="sm" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            disabled={picked === null || merge.isPending}
            onClick={() => merge.mutate()}
          >
            {merge.isPending
              ? 'Merging…'
              : picked === null
                ? 'Pick a request'
                : `Merge into #${picked.number}`}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <input
          autoFocus
          value={query}
          placeholder="Search by number, title, or anything written in it…"
          onChange={(event) => {
            setQuery(event.target.value);
            setPicked(null);
          }}
          className="w-full rounded-control border border-line bg-base px-2.5 py-1.5 fs-13 text-ink"
        />

        {query.trim() === '' ? (
          <p className="fs-12 text-muted">Type to search this workspace.</p>
        ) : search.isPending ? (
          <p className="fs-12 text-muted">Searching…</p>
        ) : hits.length === 0 ? (
          <p className="fs-12 text-muted">Nothing matched. Try a word from the request itself.</p>
        ) : (
          <ul className="flex max-h-[18rem] flex-col gap-1 overflow-y-auto">
            {hits.map((hit) => {
              const selected = picked?.id === hit.id;
              return (
                <li key={hit.id}>
                  <button
                    type="button"
                    onClick={() => setPicked({ id: hit.id, number: hit.number, title: hit.title })}
                    className={cx(
                      'w-full rounded-lg border px-2.5 py-2 text-left',
                      selected ? 'border-accent bg-accent-tint' : 'border-line hover:bg-surface-2',
                    )}
                  >
                    <span className="block fs-13 font-semibold text-ink">
                      #{hit.number} {hit.title}
                    </span>
                    <span className="block fs-11 text-muted">
                      {hit.projectName ?? 'No project'} · {hit.ownerName ?? 'Unassigned'}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <p className="fs-12 text-muted">
          #{request.number} stays where it is, marked as a duplicate and moved to Done. Its conversation is
          not moved and nothing is deleted — both requests get a trail entry saying what happened.
        </p>

        {merge.error !== null && (
          <p role="alert" className="fs-12 text-danger">
            {merge.error.message}
          </p>
        )}
      </div>
    </Modal>
  );
}

/**
 * Delete, confirmed by typing the request's number.
 *
 * The number rather than a plain "are you sure", because this is the only
 * action in the product that cannot be undone and the dialog it appears in
 * looks like every other dialog. Typing 3666 requires having read which
 * request is about to go.
 *
 * The server refuses outright for anything with logged time behind it or a
 * frozen sprint scope recording it, so a reviewer who tries to delete real
 * work gets an explanation rather than a loss — that refusal is what this
 * dialog is quietly relying on, and it is stated here so the next person does
 * not add an "are you really sure" on top of it instead.
 */
function DeleteDialog({
  workspaceId,
  request,
  open,
  onOpenChange,
}: {
  workspaceId: string;
  request: RequestRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [typed, setTyped] = useState('');
  const matches = typed.trim() === String(request.number);

  const remove = useMutation({
    mutationFn: () => deleteRequest(workspaceId, request.id, request.number),
    onSuccess: async () => {
      // Remove the dead row before navigating, or the Posts list paints it
      // once more on the way past.
      queryClient.removeQueries({ queryKey: queryKeys.request(workspaceId, request.id) });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.requests(workspaceId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.reviewQueue(workspaceId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.board(workspaceId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.backlog(workspaceId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.activity(workspaceId) }),
      ]);
      onOpenChange(false);
      void navigate('/');
    },
  });

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={`Delete #${request.number}?`}
      description="This cannot be undone."
      footer={
        <>
          <Button size="sm" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="danger"
            disabled={!matches || remove.isPending}
            onClick={() => remove.mutate()}
          >
            {remove.isPending ? 'Deleting…' : 'Delete for good'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="fs-13 text-ink-soft">
          <span className="font-semibold text-ink">{request.title}</span> and everything on it — the
          conversation, the files, its own trail — is removed permanently. The deletion itself is recorded in
          the workspace audit trail.
        </p>
        <p className="fs-13 text-ink-soft">
          If this is real work that turned out to be a duplicate, merge it instead. Nothing is lost that way.
        </p>
        <label className="flex flex-col gap-1">
          <span className="fs-12 text-muted">
            Type <span className="numeric font-semibold text-ink">{request.number}</span> to confirm
          </span>
          <input
            autoFocus
            value={typed}
            inputMode="numeric"
            onChange={(event) => setTyped(event.target.value)}
            className="w-full rounded-control border border-line bg-base px-2.5 py-1.5 fs-13 text-ink"
          />
        </label>

        {remove.error !== null && (
          <p role="alert" className="fs-12 text-danger">
            {remove.error.message}
          </p>
        )}
      </div>
    </Modal>
  );
}
